import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pagination, Dropdown } from 'rsuite';
import ls from 'localstorage-slim';
import { toast } from 'react-toastify';
import axios from 'axios';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { FaRegTrashAlt } from 'react-icons/fa';

import Loading from '../GeneralComponents/Loading';
import DataContext from '../context/InvoiceData';
import { refreshToken, getETAToken, updateUserLastLogin } from '../helpers';

function DraftDocument() {
  let navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const { setStep, setDocumentType, setDocumentTypeVersion } = useContext(DataContext);

  const [draftDocuments, setDraftDocuments] = useState([]);
  const [metaData, setMetaData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [activePage, setActivePage] = useState(1);
  const [isDeleted, setIsDeleted] = useState(false);

  const subDomain = ls.get('subDomain');

  const handleDraftNavigate = (id, type, version) => {
    setStep(5);
    setDocumentType(type === 'i' ? 'Invoice' : type === 'c' ? 'Credit Note' : 'Debit Note');
    setDocumentTypeVersion(version);
    ls.set('id', id);
    navigate('/submitInvoiceSteps');
  };

  const handleDeleteInvoice = id => {
    axios
      .delete(`${subDomain}${process.env.REACT_APP_DJANGO_BASEURL}/invoices/${id}`)
      .then(response => {
        setIsDeleted(!isDeleted);
      })
      .catch(error => {
        console.log(error);
      });
  };

  useEffect(() => {
    async function fetchData() {
      const draftToast = toast.loading('Please wait...');
      try {
        if (!ls.get('access')) {
          await refreshToken();
          await updateUserLastLogin();
        }
        if (!ls.get('ETAToken')) await getETAToken();

        let response = await axios.get(
          `${subDomain}${process.env.REACT_APP_DJANGO_BASEURL}/erp/draft_documents`,
          {
            params: {
              pageNumber: activePage,
              taxpayer: ls.get('taxpayer_id'),
            },
          }
        );

        setDraftDocuments(response.data.result);
        setMetaData(response.data.metadata);
        setIsLoading(false);

        toast.update(draftToast, {
          render: 'Draft Documents retrieved successfully',
          type: 'success',
          isLoading: false,
          autoClose: 1000,
          theme: 'colored',
        });
      } catch (error) {
        if (error.response) {
          // Request made but the server responded with an error
          console.log(error.response.data);
          toast.update(draftToast, {
            render: `${error.response.data}`,
            type: 'error',
            isLoading: false,
            autoClose: 1000,
            theme: 'colored',
          });
          console.log(error.response.data);
        } else if (error.request) {
          // Request made but no response is received from the server.
          console.log(error.request);
          toast.update(draftToast, {
            render: `${error.request}`,
            type: 'error',
            isLoading: false,
            autoClose: 1000,
            theme: 'colored',
          });
        } else {
          // Error occured while setting up the request
          console.log('Error', error.message);
          toast.update(draftToast, {
            render: `${error.message}`,
            type: 'error',
            isLoading: false,
            autoClose: 1000,
            theme: 'colored',
          });
        }
      }
    }

    fetchData();
  }, [activePage, isDeleted]);

  return isLoading ? (
    <div style={{ height: '77vh' }}>
      <Loading />
    </div>
  ) : (
    <>
      <div className='container-fluid'>
        <div className='row justify-content-center mx-2'>
          <table className='table col mt-2' dir={`${i18n.language === 'ar' && 'rtl'}`}>
            <thead
              style={{
                verticalAlign: 'middle',
              }}>
              <tr className='text-center'>
                <th scope='col'>Internal ID</th>
                <th scope='col'>Last Modified / Modified By</th>
                <th scope='col'>Type / Version</th>
                <th scope='col'>Total Value (EGP)</th>
                <th scope='col'>Issuer Branch (from)</th>
                <th scope='col'>Receiver (to)</th>
                <th scope='col'>Status</th>
                <th scope='col'></th>
              </tr>
            </thead>
            <tbody>
              {draftDocuments.map(draftDocument => (
                <tr key={draftDocument.id}>
                  <th scope='row' role='button'>
                    <div
                      className='py-2 text-center'
                      onClick={() => {
                        handleDraftNavigate(
                          draftDocument.id,
                          draftDocument.document_type,
                          draftDocument.document_type_version
                        );
                      }}>
                      {draftDocument.internal_id}
                    </div>
                  </th>
                  <td className='text-nowrap text-center'>
                    <span className='d-block fw-bold' dir='ltr'>
                      {moment(draftDocument.dateTimeReceived).format('DD/MM/YYYY h:mm a')}
                    </span>
                    <span className='d-block'>{draftDocument.modified_by}</span>
                  </td>
                  <td className='text-center'>
                    <span className='d-block fw-bold'>
                      {draftDocument.document_type === 'i'
                        ? 'Invoice'
                        : draftDocument.document_type === 'c'
                        ? 'Credit Note'
                        : draftDocument.document_type === 'd'
                        ? 'Debit Note'
                        : null}
                    </span>
                    <span className='d-block'>{draftDocument.document_type_version}</span>
                  </td>
                  <td>
                    <div className='py-2 text-center'>{draftDocument.total_amount}</div>
                  </td>
                  <td className='text-nowrap text-center'>
                    <span className='d-block fw-bold'>
                      {draftDocument.branch === '0' ? 'المقر الرئيسي' : ''}
                    </span>
                    <span className='d-block'>{draftDocument.branch}</span>
                  </td>
                  <td className='text-center'>
                    <span
                      className='d-block fw-bold text-nowrap text-truncate'
                      style={{ maxWidth: '150px', direction: 'rtl' }}>
                      {draftDocument.receiver_name}
                    </span>
                    <span className='d-block'>{draftDocument.receiver_rin}</span>
                  </td>
                  <td>
                    <div
                      className={`py-3 text-center ${
                        draftDocument.status === 'ready' ? 'text-success fw-bold' : 'text-muted'
                      }`}>
                      {draftDocument.status === 'ready' ? 'Draft (Ready)' : 'Draft'}
                    </div>
                  </td>
                  <td className='text-end' role='button'>
                    <Dropdown
                      placement={`${i18n.language === 'en' ? 'bottomEnd' : 'bottomStart'}`}
                      noCaret
                      title='&#x22EE;'>
                      <Dropdown.Item
                        icon={<FaRegTrashAlt />}
                        onClick={() => {
                          handleDeleteInvoice(draftDocument.id);
                        }}>
                        {' '}
                        Delete
                      </Dropdown.Item>
                    </Dropdown>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {draftDocuments.length === 0 && (
            <div className='d-flex justify-content-center'>
              <span>{t('document.table.noRecords')}</span>
            </div>
          )}
          <Pagination
            prev
            next
            total={metaData['totalCount']}
            limit={10}
            activePage={activePage}
            onChangePage={setActivePage}
            className='my-2 justify-content-center'
            size='md'
          />
        </div>
      </div>
    </>
  );
}

export default DraftDocument;
